import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { machineService, type OtsOrderPopulation } from '@/services/machineService'
import type { PrintIqKpiSuccess } from '@/types/printiqKpi'
import { isPrintIqKpiSuccess } from '@/types/printiqKpi'

const TEN_MINUTES_MS = 10 * 60 * 1000

export const usePrintIqKpiStore = defineStore('printiqKpi', () => {
  const data = ref<PrintIqKpiSuccess | null>(null)
  const fetchedAt = ref<number | null>(null)
  const loading = ref(false)
  const available = ref(false)
  /** Nav-bar OTS metrics population selector — Combined is the primary metric for OTS. Each reads its own admin-refreshed cached snapshot. */
  const population = ref<OtsOrderPopulation>('combined')

  const isStale = computed(() => {
    if (fetchedAt.value === null) return true
    return Date.now() - fetchedAt.value > TEN_MINUTES_MS
  })

  /**
   * Fetch the cached OTS KPI for the nav bar (current-month snapshot) for the selected population.
   * Sends no dates so the API returns the cached snapshot updated by "PrintIQ KPI refresh" / schedule:piq-kpi.
   */
  async function fetchKpi(): Promise<void> {
    loading.value = true
    try {
      const response = await machineService.getPrintIqKpi({ order_population: population.value })
      if (isPrintIqKpiSuccess(response)) {
        data.value = response
        fetchedAt.value = Date.now()
        available.value = true
      } else {
        available.value = false
        data.value = null
        fetchedAt.value = Date.now()
      }
    } catch (err) {
      available.value = false
      data.value = null
    } finally {
      loading.value = false
    }
  }

  /** Switch nav-bar population and refetch (Standard/Custom, Series, or Combined). */
  async function setPopulation(pop: OtsOrderPopulation): Promise<void> {
    if (population.value === pop) return
    population.value = pop
    await fetchKpi()
  }

  return {
    data,
    fetchedAt,
    loading,
    available,
    population,
    isStale,
    fetchKpi,
    setPopulation,
  }
})
